import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../../lib/i18n';
import { api } from '../../lib/api.ts';
import { Tabs, TabList, Tab, TabPanel } from '../../components/ui/Tabs.tsx';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card.tsx';
import { StatsGrid, StatCard } from '../../components/ui/Stats.tsx';
import { DataTable } from '../../components/ui/DataTable.tsx';
import { Button } from '../../components/ui/Button.tsx';
import { Modal } from '../../components/ui/Modal.tsx';
import { Input } from '../../components/ui/Input.tsx';
import { Select } from '../../components/ui/Select.tsx';
import { PageHeader } from '../../components/ui/PageHeader.tsx';
import { SectionHeader } from '../../components/ui/SectionHeader.tsx';
import { EmptyState } from '../../components/ui/EmptyState.tsx';
import { RefreshButton } from '../../components/ui/RefreshButton.tsx';
import { Toggle } from '../../components/ui/Toggle.tsx';
import { Badge } from '../../components/ui/Badge.tsx';
import {
  Server, Play, StopCircle, XCircle, Trash2, Copy, Database,
  Shield, Settings, Search, Plus, RefreshCw, AlertCircle, CheckCircle2,
  Clock, Globe, Layers, Box, HardDrive,
} from '../../components/ui/Icon.tsx';
import { themedConfirm, themedPrompt } from '../../lib/themed-dialogs';

// ── Helpers ───────────────────────────────────────────────────────────────────

const MB = 1024 * 1024;
const GB = 1024 * MB;


function fmtUptime(secs: number): string {
  if (!secs) return '—';
  const d = Math.floor(secs / 86400);
  const h = Math.floor((secs % 86400) / 3600);
  const m = Math.floor((secs % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function fmtDate(iso: string): string {
  try { return new Date(iso).toLocaleString('cs-CZ'); } catch { return iso; }
}

function statusBadge(status?: string) {
  switch (status) {
    case 'running': return <Badge variant='success'>running</Badge>;
    case 'stopped':
    case 'off': return <Badge variant='secondary'>stopped</Badge>;
    case 'paused': return <Badge variant='warning'>paused</Badge>;
    case 'locked': return <Badge variant='error'>locked</Badge>;
    default: return <Badge variant='secondary'>{status ?? 'unknown'}</Badge>;
  }
}

function nodeStatusBadge(status?: string) {
  switch (status) {
    case 'online': return <Badge variant='success'>online</Badge>;
    case 'offline': return <Badge variant='error'>offline</Badge>;
    default: return <Badge variant='secondary'>{status ?? '—'}</Badge>;
  }
}

// ── Types ─────────────────────────────────────────────────────────────────────

type ServerEntry = {
  id: string;
  label: string;
  url: string;
  api_token?: string;
  verify_ssl: boolean;
  created_at?: string;
  updated_at?: string;
  cluster_role?: 'home' | 'remote';
  write_mode?: 'oma' | 'platform' | 'legacy_direct';
  token_role?: string;
}

interface OmaRequest {
  id: string;
  server_id: string;
  tool: string;
  action: string;
  status: string;
  risk?: string;
  reason?: string | null;
  requested_by?: string;
  requested_at?: string;
  approved_by?: string | null;
  expires_at?: string | null;
  error?: string | null;
}

interface OverviewResult {
  cluster?: { nodes: number; nodes_online: number; total_cpus: number; ram_total: string; ram_used: string; ram_pct: string };
  vms?: { total: number; running: number; stopped: number };
  containers?: { total: number; running: number; stopped: number };
  nodes?: Array<{ node: string; status: string; cpus: number; cpu_pct: string; ram: string; disk: string; uptime: string }>;
  server?: string;
}

type VmEntry = {
  vmid: number;
  name?: string;
  node: string;
  status?: string;
  cpus?: string | number;
  ram?: string;
  uptime?: string;
}

// ── Load state + UI feedback ──────────────────────────────────────────────────

type LoadState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ok' }
  | { kind: 'timeout' }
  | { kind: 'error'; message: string };

/**
 * Volá MCP tool s explicitním timeoutem (default 30s) a vrátí strukturovaný
 * stav. Hodnoty se přepínají takto:
 *   - request hodí → { kind: 'error', message }
 *   - timeout uběhne → { kind: 'timeout' }
 *   - tool vrátí { ok: false, error } → { kind: 'error', message: error }
 *   - tool vrátí { ok: true, ... } → { kind: 'ok' } a callback dostane data
 */
async function callWithStatus<T = Record<string, unknown>>(
  tool: string,
  args: Record<string, unknown>,
  onOk: (r: T) => void,
  timeoutMs = 30_000,
): Promise<LoadState> {
  type R = T & { ok?: boolean; error?: string };
  const timeoutP = new Promise<{ __timeout: true }>((resolve) =>
    setTimeout(() => resolve({ __timeout: true }), timeoutMs)
  );
  try {
    const r = await Promise.race([
      api.mcp(tool, args) as Promise<R>,
      timeoutP,
    ]);
    if ('__timeout' in r) return { kind: 'timeout' };
    if (r.ok === false) return { kind: 'error', message: r.error ?? i18n.t('proxmox.unknownError') };
    onOk(r as T);
    return { kind: 'ok' };
  } catch (e) {
    return { kind: 'error', message: e instanceof Error ? e.message : String(e) };
  }
}

/** Sdílený proužek s viditelným stavem load akce (loading/timeout/error). */
function LoadBanner({ state, server }: { state: LoadState; server?: string }) {
  const { t } = useTranslation();
  if (state.kind === 'idle' || state.kind === 'ok') return null;
  if (state.kind === 'loading') {
    return (
      <div className='flex items-center gap-2 rounded-md border border-gray-700/50 bg-gray-900/30 px-3 py-2 text-sm text-gray-400'>
        <RefreshCw size={14} className='animate-spin' />
        {t('proxmox.loadingLabel')}{server ? ` (${server})` : ''}…
      </div>
    );
  }
  if (state.kind === 'timeout') {
    return (
      <div className='flex items-start gap-2 rounded-md border border-amber-700/40 bg-amber-900/20 px-3 py-2 text-sm text-amber-300'>
        <Clock size={14} className='mt-0.5 flex-shrink-0' />
        <div>
          <strong>{t('proxmox.timeoutTitle')}{server ? ` — ${server}` : ''}.</strong>{' '}
          {t('proxmox.timeoutHint')}
        </div>
      </div>
    );
  }
  // error
  return (
    <div className='flex items-start gap-2 rounded-md border border-red-700/40 bg-red-900/20 px-3 py-2 text-sm text-red-300'>
      <AlertCircle size={14} className='mt-0.5 flex-shrink-0' />
      <div>
        <strong>{t('proxmox.errorLabel')}{server ? ` — ${server}` : ''}:</strong> <span className='break-all'>{state.message}</span>
      </div>
    </div>
  );
}

// ── Shared state ──────────────────────────────────────────────────────────────

function useServers() {
  const [servers, setServers] = useState<ServerEntry[]>([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.mcp<{ ok: boolean; error?: string; servers?: ServerEntry[] }>(
        'proxmox.servers_list',
        {},
      );
      if (r.ok) setServers(r.servers ?? []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  return { servers, loading, reload: load };
}

function pickServer(servers: ServerEntry[], serverId?: string): ServerEntry | undefined {
  if (serverId) return servers.find((s) => s.id === serverId);
  return servers[0];
}

// ═══════════════════════════════════════════════════════════════════════════════
// TAB: Přehled
// ═══════════════════════════════════════════════════════════════════════════════

function TabOverview({ servers }: { servers: ServerEntry[] }) {
  const { t } = useTranslation();
  const [overview, setOverview] = useState<OverviewResult | null>(null);
  const [loadState, setLoadState] = useState<LoadState>({ kind: 'idle' });
  const [serverId, setServerId] = useState(servers[0]?.id);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Array<{
    type: 'vm' | 'ct'; node: string; vmid: number; name?: string; status?: string; ram?: string; uptime?: string;
  }> | null>(null);
  const [searchState, setSearchState] = useState<LoadState>({ kind: 'idle' });

  const server = pickServer(servers, serverId);

  const loadOverview = useCallback(async () => {
    if (!server) return;
    setLoadState({ kind: 'loading' });
    setOverview(null);
    const s = await callWithStatus<OverviewResult>(
      'proxmox.audit',
      { action: 'overview', server_id: server.id },
      (r) => setOverview(r),
    );
    setLoadState(s);
  }, [server]);

  useEffect(() => { loadOverview(); }, [loadOverview]);

  const doSearch = useCallback(async () => {
    if (!server || !searchQuery.trim()) return;
    setSearchState({ kind: 'loading' });
    setSearchResults(null);
    const s = await callWithStatus<{ results?: Array<{ type: 'vm' | 'ct'; node: string; vmid: number; name?: string; status?: string; ram?: string; uptime?: string }> }>(
      'proxmox.find',
      { query: searchQuery.trim() },
      (r) => setSearchResults(r.results ?? []),
    );
    setSearchState(s);
  }, [server, searchQuery]);

  return (
    <div className='space-y-6'>
      {/* Server selector */}
      <div className='flex items-center gap-3'>
        <label className='text-sm text-gray-400'>{t('proxmox.serverLabel')}</label>
        <Select
          value={serverId ?? ''}
          onChange={(e) => { setServerId(e.target.value); setSearchResults(null); }}
          options={servers.map((s) => ({ value: s.id, label: s.label }))}
          placeholder={t('proxmox.selectServerPlaceholder')}
        />
        <RefreshButton onRefresh={loadOverview} loading={loadState.kind === 'loading'} />
      </div>

      {!server && <EmptyState icon={<AlertCircle size={24} />} title={t('proxmox.noServerConfiguredTitle')} />}

      {server && <LoadBanner state={loadState} server={server.label} />}

      {server && overview && (
        <>
          {/* Cluster stats */}
          {overview.cluster && (
            <StatsGrid columns={4}>
              <StatCard label={t('proxmox.statNodes')} value={overview.cluster.nodes_online} sub={t('proxmox.totalCountSuffix', { count: overview.cluster.nodes })} icon={Server} variant='info' />
              <StatCard label={t('proxmox.statCpu')} value={overview.cluster.total_cpus} sub={t('proxmox.totalCoresSuffix')} icon={Settings} variant='info' />
              <StatCard label={t('proxmox.statRam')} value={overview.cluster.ram_used} sub={`${overview.cluster.ram_pct} / ${overview.cluster.ram_total}`} icon={Database} variant='info' />
              <StatCard label={t('proxmox.statVmRunning')} value={overview.vms?.running ?? 0} sub={t('proxmox.totalCountSuffix', { count: overview.vms?.total ?? 0 })} icon={Play} variant='success' />
            </StatsGrid>
          )}

          {/* VM + CT summary */}
          <div className='grid grid-cols-2 gap-4'>
            <Card>
              <CardContent className='p-5 flex items-center gap-4'>
                <div className='w-12 h-12 rounded-xl bg-blue-600/20 flex items-center justify-center'>
                  <Server size={24} className='text-blue-400' />
                </div>
                <div>
                  <p className='text-2xl font-bold text-white'>{overview.vms?.total ?? 0}</p>
                  <p className='text-xs text-gray-500'>{t('proxmox.vmTotalLabel')}</p>
                  <p className='text-xs text-emerald-400'>{overview.vms?.running ?? 0} running</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className='p-5 flex items-center gap-4'>
                <div className='w-12 h-12 rounded-xl bg-purple-600/20 flex items-center justify-center'>
                  <HardDrive size={24} className='text-purple-400' />
                </div>
                <div>
                  <p className='text-2xl font-bold text-white'>{overview.containers?.total ?? 0}</p>
                  <p className='text-xs text-gray-500'>{t('proxmox.ctTotalLabel')}</p>
                  <p className='text-xs text-emerald-400'>{overview.containers?.running ?? 0} running</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Nodes table */}
          {overview.nodes && overview.nodes.length > 0 && (
            <div>
              <SectionHeader title={t('proxmox.nodesLabel')} />
              <DataTable
                columns={[
                  { key: 'node', label: t('proxmox.colNode') },
                  { key: 'status', label: t('proxmox.colStatus'), render: (_, i) => nodeStatusBadge(overview!.nodes![i]?.status) },
                  { key: 'cpus', label: t('proxmox.colCpu') },
                  { key: 'cpu_pct', label: t('proxmox.colCpuPct') },
                  { key: 'ram', label: t('proxmox.colRam') },
                  { key: 'disk', label: t('proxmox.colDisk') },
                  { key: 'uptime', label: t('proxmox.colUptime') },
                ]}
                data={overview.nodes}
                rowKey={(n) => n.node}
              />
            </div>
          )}

          {/* Quick search */}
          <div>
            <SectionHeader title={t('proxmox.quickSearchHeader')} />
            <div className='flex gap-2'>
              <Input
                placeholder={t('proxmox.searchVmPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && doSearch()}
                className='flex-1'
              />
              <Button onClick={doSearch} loading={searchState.kind === 'loading'} icon={<Search size={12} />}>{t('proxmox.searchButton')}</Button>
            </div>

            <div className='mt-2'>
              <LoadBanner state={searchState} />
            </div>

            {searchResults && searchResults.length > 0 && (
              <div className='mt-3'>
                <DataTable
                  columns={[
                    { key: 'type', label: t('proxmox.colType'), render: (_, i) => (
                      <Badge variant={searchResults[i]?.type === 'vm' ? 'info' : 'purple'}>
                        {searchResults[i]?.type?.toUpperCase()}
                      </Badge>
                    )},
                    { key: 'node', label: t('proxmox.colNode') },
                    { key: 'vmid', label: t('proxmox.colVmid') },
                    { key: 'name', label: t('proxmox.colName'), render: (_, i) => <span className='font-medium text-white'>{searchResults[i]?.name ?? `vm-${searchResults[i]?.vmid}`}</span> },
                    { key: 'status', label: t('proxmox.colStatus'), render: (_, i) => statusBadge(searchResults[i]?.status) },
                    { key: 'ram', label: t('proxmox.colRam') },
                    { key: 'uptime', label: t('proxmox.colUptime') },
                  ]}
                  data={searchResults}
                  rowKey={(r) => `${r.node}-${r.vmid}`}
                />
              </div>
            )}

            {searchResults?.length === 0 && searchQuery && (
              <p className='text-sm text-gray-500 mt-2'>{t('proxmox.noSearchResults', { query: searchQuery })}</p>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TAB: VM a CT
// ═══════════════════════════════════════════════════════════════════════════════

function TabVmCt({ servers }: { servers: ServerEntry[] }) {
  const { t } = useTranslation();
  const [serverId, setServerId] = useState(servers[0]?.id);
  const [vmType, setVmType] = useState<'vm' | 'ct' | 'all'>('all');
  const [vms, setVms] = useState<VmEntry[]>([]);
  const [loadState, setLoadState] = useState<LoadState>({ kind: 'idle' });
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [confirmModal, setConfirmModal] = useState<{
    title: string; message: string; onConfirm: () => void; variant: 'danger' | 'warning';
  } | null>(null);

  const server = pickServer(servers, serverId);

  const loadVms = useCallback(async () => {
    if (!server) return;
    setLoadState({ kind: 'loading' });
    setVms([]);
    if (vmType === 'all') {
      // Volání paralelně, ale stav agregujeme: timeout/error v jednom = error pro celek.
      const all: VmEntry[] = [];
      const [vmsState, ctsState] = await Promise.all([
        callWithStatus<{ vms?: VmEntry[] }>(
          'proxmox.audit', { action: 'vms', server_id: server.id },
          (r) => { if (r.vms) all.push(...r.vms.map((v) => ({ ...v, node: v.node }))); },
        ),
        callWithStatus<{ containers?: VmEntry[] }>(
          'proxmox.audit', { action: 'containers', server_id: server.id },
          (r) => { if (r.containers) all.push(...r.containers.map((c) => ({ ...c, node: c.node }))); },
        ),
      ]);
      setVms(all);
      // Worst-case: pokud cokoliv selhalo, ukaž to. Preferuj timeout > error > ok.
      const worst: LoadState =
        vmsState.kind === 'timeout' || ctsState.kind === 'timeout' ? { kind: 'timeout' }
        : vmsState.kind === 'error' ? vmsState
        : ctsState.kind === 'error' ? ctsState
        : { kind: 'ok' };
      setLoadState(worst);
    } else {
      const action = vmType === 'vm' ? 'vms' : 'containers';
      const key = vmType === 'vm' ? 'vms' : 'containers';
      const s = await callWithStatus<Record<string, unknown>>(
        'proxmox.audit', { action, server_id: server.id },
        (r) => setVms(((r[key] ?? []) as VmEntry[]).map((v) => ({ ...v, node: v.node }))),
      );
      setLoadState(s);
    }
  }, [server, vmType]);

  useEffect(() => { loadVms(); }, [loadVms]);

  const doAction = useCallback(async (action: string, vmid: number, name?: string) => {
    if (!server) return;
    setActionLoading(`${vmid}-${action}`);
    try {
      const r = await api.mcp<{
        ok?: boolean;
        error?: string;
        code?: string;
        requires_oma?: boolean;
      }>('proxmox.manage', {
        action,
        server_id: server.id,
        vmid,
      });
      if (r?.requires_oma || r?.code === 'oma_required') {
        // Home / OMA connector — založ request pro operátora
        const req = await api.mcp<{
          ok?: boolean;
          request_id?: string;
          needed?: boolean;
          error?: string;
          message?: string;
        }>('proxmox.request_write', {
          server_id: server.id,
          tool: 'proxmox.manage',
          action,
          args: { server_id: server.id, vmid, action },
          reason: `UI ${action} ${name ?? vmid}`,
          risk: action === 'destroy' || action === 'kill' ? 'high' : 'medium',
        });
        if (req?.ok && req.request_id) {
          alert(
            t(
              'proxmox.omaRequestCreated',
              'OMA: požadavek {{id}} založen. Schválení přes ACMLink (ne v Proxmox tabu — ten je log).',
              { id: req.request_id.slice(0, 8) },
            ),
          );
        } else if (req?.ok && req.needed === false) {
          alert(req.message ?? t('proxmox.omaNotNeeded', 'OMA netřeba — zkus znovu s write ACL.'));
        } else {
          alert(t('proxmox.genericErrorAlert', { error: req?.error ?? r.error }));
        }
      } else if (!r?.ok) {
        alert(t('proxmox.genericErrorAlert', { error: r?.error }));
      } else {
        await loadVms();
      }
    } catch (e) { console.error(e); alert(String(e)); }
    finally { setActionLoading(null); }
  }, [server, loadVms, t]);

  const handleAction = useCallback((action: string, vm: VmEntry) => {

    if (action === 'destroy') {
      setConfirmModal({
        title: t('proxmox.deleteVmConfirmTitle', { vmid: vm.vmid }),
        message: t('proxmox.deleteVmConfirmMessage', { name: vm.name ?? vm.vmid, node: vm.node }),
        onConfirm: () => doAction(action, vm.vmid, vm.name),
        variant: 'danger',
      });
    } else if (action === 'kill') {
      setConfirmModal({
        title: t('proxmox.killVmConfirmTitle', { vmid: vm.vmid }),
        message: t('proxmox.killVmConfirmMessage', { name: vm.name ?? vm.vmid }),
        onConfirm: () => doAction(action, vm.vmid, vm.name),
        variant: 'warning',
      });
    } else {
      doAction(action, vm.vmid, vm.name);
    }
  }, [doAction]);

  return (
    <div className='space-y-4'>
      {/* Filters */}
      <div className='flex items-center gap-3'>
        <label className='text-sm text-gray-400'>{t('proxmox.serverLabel')}</label>
        <Select
          value={serverId ?? ''}
          onChange={(e) => setServerId(e.target.value)}
          options={servers.map((s) => ({ value: s.id, label: s.label }))}
          placeholder={t('proxmox.selectServerPlaceholder')}
        />
        <div className='flex gap-1 ml-auto'>
          {(['all', 'vm', 'ct'] as const).map((vt) => (
            <Button
              key={vt}
              variant={vmType === vt ? 'primary' : 'secondary'}
              onClick={() => setVmType(vt)}
            >
              {vt === 'all' ? t('proxmox.allLabel') : vt === 'vm' ? 'VM' : 'CT'}
            </Button>
          ))}
        </div>
        <RefreshButton onRefresh={loadVms} loading={loadState.kind === 'loading'} />
      </div>

      {!server && <EmptyState icon={<AlertCircle size={24} />} title={t('proxmox.noServerConfiguredTitle')} />}

      {server && <LoadBanner state={loadState} server={server.label} />}

      {server && (
        <>
          {vms.length === 0 && loadState.kind === 'ok' ? (
            <EmptyState icon={<Box size={24} />} title={t('proxmox.noRecordsTitle')} />
          ) : vms.length === 0 ? null : (
            <DataTable
              columns={[
                { key: 'vmid', label: t('proxmox.colVmid') },
                { key: 'name', label: t('proxmox.colName'), render: (_, i) => <span className='font-medium text-white'>{vms[i].name ?? `vm-${vms[i].vmid}`}</span> },
                { key: 'node', label: t('proxmox.colNode') },
                { key: 'status', label: t('proxmox.colStatus'), render: (_, i) => statusBadge(vms[i].status) },
                { key: 'cpus', label: t('proxmox.colCpu') },
                { key: 'ram', label: t('proxmox.colRam') },
                { key: 'uptime', label: t('proxmox.colUptime') },
                {
                  key: 'actions', label: '', render: (_, i) => {
                    const vm = vms[i];
                    const isRunning = vm.status === 'running';
                    const isStopped = vm.status === 'stopped' || vm.status === 'off';
                    return (
                      <div className='flex items-center gap-2'>
                        {isStopped && (
                          <Button
                            variant='success'
                            size='sm'
                            loading={actionLoading === `${vm.vmid}-start`}
                            onClick={() => handleAction('start', vm)}
                            icon={<Play size={12} />}
                          >
                            {t('proxmox.actionStart')}
                          </Button>
                        )}
                        {isRunning && (
                          <>
                            <Button
                              variant='warning'
                              size='sm'
                              loading={actionLoading === `${vm.vmid}-stop`}
                              onClick={() => handleAction('stop', vm)}
                              icon={<StopCircle size={12} />}
                            >
                              {t('proxmox.actionStop')}
                            </Button>
                            <Button
                              variant='warning'
                              size='sm'
                              loading={actionLoading === `${vm.vmid}-restart`}
                              onClick={() => handleAction('restart', vm)}
                              icon={<RefreshCw size={12} />}
                            >
                              {t('proxmox.actionRestart')}
                            </Button>
                            <Button
                              variant='danger'
                              size='sm'
                              loading={actionLoading === `${vm.vmid}-kill`}
                              onClick={() => handleAction('kill', vm)}
                              icon={<XCircle size={12} />}
                            >
                              {t('proxmox.actionKill')}
                            </Button>
                          </>
                        )}
                        {isRunning && (
                          <Button
                            variant='danger'
                            size='sm'
                            loading={actionLoading === `${vm.vmid}-destroy`}
                            onClick={() => handleAction('destroy', vm)}
                            icon={Trash2}
                          >
                            {t('proxmox.actionDestroy')}
                          </Button>
                        )}
                      </div>
                    );
                  },
                },
              ]}
              data={vms}
              rowKey={(v) => `${v.node}-${v.vmid}`}
            />
          )}
        </>
      )}

      {/* Confirmation modal */}
      <Modal
        open={!!confirmModal}
        onClose={() => setConfirmModal(null)}
        title={confirmModal?.title}
        size='sm'
        footer={
          confirmModal ? (
            <>
              <Button variant='secondary' onClick={() => setConfirmModal(null)}>{t('proxmox.cancelButton')}</Button>
              <Button
                variant={confirmModal.variant === 'danger' ? 'danger' : 'warning'}
                onClick={() => { confirmModal.onConfirm(); setConfirmModal(null); }}
              >
                {t('proxmox.confirmButton')}
              </Button>
            </>
          ) : undefined
        }
      >
        <p className='text-sm text-gray-300'>{confirmModal?.message}</p>
      </Modal>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TAB: Vytvořit VM/CT
// ═══════════════════════════════════════════════════════════════════════════════

function TabCreate({ servers }: { servers: ServerEntry[] }) {
  const { t } = useTranslation();
  const [serverId, setServerId] = useState(servers[0]?.id);
  const [type, setType] = useState<'vm' | 'ct'>('vm');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<Record<string, unknown> | null>(null);
  const [error, setError] = useState<string | null>(null);

  // VM form state
  const [vmName, setVmName] = useState('');
  const [vmVmid, setVmVmid] = useState('');
  const [vmNode, setVmNode] = useState('');
  const [vmCpu, setVmCpu] = useState('1');
  const [vmMem, setVmMem] = useState('1024');
  const [vmDisk, setVmDisk] = useState('8');
  const [vmNet, setVmNet] = useState('bridge=vmbr0');
  const [vmIso, setVmIso] = useState('');
  const [vmStorage, setVmStorage] = useState('local-lvm');
  const [vmSockets, setVmSockets] = useState('1');
  const [vmArch, setVmArch] = useState('x86_64');

  // CT form state
  const [ctName, setCtName] = useState('');
  const [ctVmid, setCtVmid] = useState('');
  const [ctNode, setCtNode] = useState('');
  const [ctCpu, setCtCpu] = useState('1');
  const [ctMem, setCtMem] = useState('512');
  const [ctDisk, setCtDisk] = useState('2');
  const [ctTemplate, setCtTemplate] = useState('default');
  const [ctPassword, setCtPassword] = useState('');
  const [ctUnprivileged, setCtUnprivileged] = useState(true);

  // Clone form state
  const [cloneMode, setCloneMode] = useState<'create' | 'clone'>('create');
  const [cloneSource, setCloneSource] = useState('');
  const [cloneNewName, setCloneNewName] = useState('');
  const [cloneNewVmid, setCloneNewVmid] = useState('');
  const [cloneType, setCloneType] = useState<'vm' | 'ct'>('vm');

  const server = pickServer(servers, serverId);

  const handleCreate = useCallback(async () => {
    if (!server) return;
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      if (type === 'vm') {
        const args: Record<string, unknown> = {
          action: 'create',
          server_id: server.id,
          name: vmName || undefined,
          vmid: vmVmid ? Number(vmVmid) : undefined,
          node: vmNode || undefined,
          cores: Number(vmCpu) || undefined,
          memory: Number(vmMem) || undefined,
          storage: vmStorage || undefined,
          disk: Number(vmDisk) || undefined,
          network: vmNet || undefined,
          iso: vmIso || undefined,
          sockets: Number(vmSockets) || undefined,
          arch: vmArch || undefined,
        };
        const r = await api.mcp<{ ok: boolean; error?: string } & Record<string, unknown>>('proxmox.create_vm', args);
        if (r.ok) setResult(r);
        else setError(r.error ?? t('proxmox.unknownError'));
      } else {
        const args: Record<string, unknown> = {
          action: 'create',
          server_id: server.id,
          name: ctName || undefined,
          vmid: ctVmid ? Number(ctVmid) : undefined,
          node: ctNode || undefined,
          cores: Number(ctCpu) || undefined,
          memory: Number(ctMem) || undefined,
          disk: Number(ctDisk) || undefined,
          template: ctTemplate || undefined,
          password: ctPassword || undefined,
          unprivileged: ctUnprivileged ? 1 : 0,
        };
        const r = await api.mcp<{ ok: boolean; error?: string } & Record<string, unknown>>('proxmox.create_ct', args);
        if (r.ok) setResult(r);
        else setError(r.error ?? t('proxmox.unknownError'));
      }
    } catch (e) { setError(String(e)); }
    finally { setLoading(false); }
  }, [server, type, vmName, vmVmid, vmNode, vmCpu, vmMem, vmDisk, vmNet, vmIso, vmStorage, vmSockets, vmArch,
    ctName, ctVmid, ctNode, ctCpu, ctMem, ctDisk, ctTemplate, ctPassword, ctUnprivileged]);

  const handleClone = useCallback(async () => {
    if (!server) return;
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const args: Record<string, unknown> = {
        action: 'clone',
        server_id: server.id,
        source_vmid: Number(cloneSource),
        new_vmid: cloneNewVmid ? Number(cloneNewVmid) : undefined,
        name: cloneNewName || undefined,
        type: cloneType,
      };
      const r = await api.mcp<{ ok: boolean; error?: string } & Record<string, unknown>>('proxmox.clone', args);
      if (r.ok) setResult(r);
      else setError(r.error ?? t('proxmox.unknownError'));
    } catch (e) { setError(String(e)); }
    finally { setLoading(false); }
  }, [server, cloneSource, cloneNewName, cloneNewVmid, cloneType]);

  return (
    <div className='space-y-6'>
      {/* Server + type selector */}
      <div className='flex items-center gap-3'>
        <label className='text-sm text-gray-400'>{t('proxmox.serverLabel')}</label>
        <Select
          value={serverId ?? ''}
          onChange={(e) => setServerId(e.target.value)}
          options={servers.map((s) => ({ value: s.id, label: s.label }))}
          placeholder={t('proxmox.selectServerPlaceholder')}
        />
      </div>

      {!server && <EmptyState icon={<AlertCircle size={24} />} title={t('proxmox.noServerConfiguredTitle')} />}

      {server && (
        <>
          {/* Mode tabs */}
          <div className='flex gap-2'>
            <Button variant={cloneMode === 'create' ? 'primary' : 'secondary'} onClick={() => setCloneMode('create')}>
              <Plus size={14} /> {t('proxmox.newLabel')}
            </Button>
            <Button variant={cloneMode === 'clone' ? 'primary' : 'secondary'} onClick={() => setCloneMode('clone')}>
              <Copy size={14} /> {t('proxmox.cloneLabel')}
            </Button>
          </div>

          {cloneMode === 'create' ? (
            <>
              {/* VM/CT type toggle */}
              <div className='flex gap-2'>
                <Button variant={type === 'vm' ? 'primary' : 'secondary'} onClick={() => setType('vm')}>
                  {t('proxmox.vmQemuLabel')}
                </Button>
                <Button variant={type === 'ct' ? 'primary' : 'secondary'} onClick={() => setType('ct')}>
                  {t('proxmox.ctLxcLabel')}
                </Button>
              </div>

              {/* Common fields */}
              <div className='grid grid-cols-2 md:grid-cols-4 gap-4'>
                <div>
                  <label className='text-xs text-gray-500 mb-1 block'>{t('proxmox.nameFieldLabel')}</label>
                  <Input
                    value={type === 'vm' ? vmName : ctName}
                    onChange={(e) => type === 'vm' ? setVmName(e.target.value) : setCtName(e.target.value)}
                    placeholder={t('proxmox.vmNamePlaceholder')}
                  />
                </div>
                <div>
                  <label className='text-xs text-gray-500 mb-1 block'>{t('proxmox.vmidOptionalLabel')}</label>
                  <Input
                    value={type === 'vm' ? vmVmid : ctVmid}
                    onChange={(e) => type === 'vm' ? setVmVmid(e.target.value) : setCtVmid(e.target.value)}
                    placeholder={t('proxmox.autoPlaceholder')}
                    type='number'
                  />
                </div>
                <div>
                  <label className='text-xs text-gray-500 mb-1 block'>{t('proxmox.nodeOptionalLabel')}</label>
                  <Input
                    value={type === 'vm' ? vmNode : ctNode}
                    onChange={(e) => type === 'vm' ? setVmNode(e.target.value) : setCtNode(e.target.value)}
                    placeholder={t('proxmox.autoPlaceholder')}
                  />
                </div>
                <div>
                  <label className='text-xs text-gray-500 mb-1 block'>{t('proxmox.cpuCoresLabel')}</label>
                  <Input value={type === 'vm' ? vmCpu : ctCpu} onChange={(e) => type === 'vm' ? setVmCpu(e.target.value) : setCtCpu(e.target.value)} type='number' />
                </div>
              </div>

              <div className='grid grid-cols-2 md:grid-cols-4 gap-4'>
                <div>
                  <label className='text-xs text-gray-500 mb-1 block'>{t('proxmox.ramMbLabel')}</label>
                  <Input value={type === 'vm' ? vmMem : ctMem} onChange={(e) => type === 'vm' ? setVmMem(e.target.value) : setCtMem(e.target.value)} type='number' />
                </div>
                <div>
                  <label className='text-xs text-gray-500 mb-1 block'>{t('proxmox.diskGbLabel')}</label>
                  <Input value={type === 'vm' ? vmDisk : ctDisk} onChange={(e) => type === 'vm' ? setVmDisk(e.target.value) : setCtDisk(e.target.value)} type='number' />
                </div>
                {type === 'vm' ? (
                  <>
                    <div>
                      <label className='text-xs text-gray-500 mb-1 block'>{t('proxmox.storageLabel')}</label>
                      <Input value={vmStorage} onChange={(e) => setVmStorage(e.target.value)} placeholder='local-lvm' />
                    </div>
                    <div>
                      <label className='text-xs text-gray-500 mb-1 block'>{t('proxmox.isoImageLabel')}</label>
                      <Input value={vmIso} onChange={(e) => setVmIso(e.target.value)} placeholder='iso:iso/ubuntu-24.04.iso' />
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <label className='text-xs text-gray-500 mb-1 block'>{t('proxmox.templateLabel')}</label>
                      <Input value={ctTemplate} onChange={(e) => setCtTemplate(e.target.value)} placeholder='default' />
                    </div>
                    <div>
                      <label className='text-xs text-gray-500 mb-1 block'>{t('proxmox.rootPasswordLabel')}</label>
                      <Input value={ctPassword} onChange={(e) => setCtPassword(e.target.value)} type='password' placeholder={t('proxmox.rootPasswordPlaceholder')} />
                    </div>
                    <div>
                      <label className='text-xs text-gray-500 mb-1 block'>{t('proxmox.unprivilegedLabel')}</label>
                      <Toggle
                        value={ctUnprivileged}
                        onSave={(v) => setCtUnprivileged(v)}
                        label={t('proxmox.unprivilegedHint')}
                      />
                    </div>
                  </>
                )}
              </div>

              {type === 'vm' && (
                <div className='grid grid-cols-2 md:grid-cols-4 gap-4'>
                  <div>
                    <label className='text-xs text-gray-500 mb-1 block'>{t('proxmox.networkLabel')}</label>
                    <Input value={vmNet} onChange={(e) => setVmNet(e.target.value)} placeholder='bridge=vmbr0' />
                  </div>
                  <div>
                    <label className='text-xs text-gray-500 mb-1 block'>{t('proxmox.socketsLabel')}</label>
                    <Input value={vmSockets} onChange={(e) => setVmSockets(e.target.value)} type='number' />
                  </div>
                  <div>
                    <label className='text-xs text-gray-500 mb-1 block'>{t('proxmox.architectureLabel')}</label>
                    <Input value={vmArch} onChange={(e) => setVmArch(e.target.value)} placeholder='x86_64' />
                  </div>
                </div>
              )}

              <Button onClick={handleCreate} loading={loading} icon={<Plus size={12} />}>
                {t('proxmox.createVmCtButton')} {type === 'vm' ? 'VM' : 'CT'}
              </Button>
            </>
          ) : (
            /* Clone mode */
            <div className='space-y-4'>
              <div className='grid grid-cols-2 md:grid-cols-4 gap-4'>
                <div>
                  <label className='text-xs text-gray-500 mb-1 block'>{t('proxmox.cloneTypeLabel')}</label>
                  <Select value={cloneType} onChange={(e) => setCloneType(e.target.value as 'vm' | 'ct')}>
                    <option value='vm'>{t('proxmox.vmQemuLabel')}</option>
                    <option value='ct'>{t('proxmox.ctLxcLabel')}</option>
                  </Select>
                </div>
                <div>
                  <label className='text-xs text-gray-500 mb-1 block'>{t('proxmox.sourceVmidLabel')}</label>
                  <Input value={cloneSource} onChange={(e) => setCloneSource(e.target.value)} type='number' placeholder='100' />
                </div>
                <div>
                  <label className='text-xs text-gray-500 mb-1 block'>{t('proxmox.newNameLabel')}</label>
                  <Input value={cloneNewName} onChange={(e) => setCloneNewName(e.target.value)} placeholder='my-vm-clone' />
                </div>
                <div>
                  <label className='text-xs text-gray-500 mb-1 block'>{t('proxmox.newVmidOptionalLabel')}</label>
                  <Input value={cloneNewVmid} onChange={(e) => setCloneNewVmid(e.target.value)} type='number' placeholder={t('proxmox.autoPlaceholder')} />
                </div>
              </div>
              <Button onClick={handleClone} loading={loading} icon={<Copy size={12} />}>
                {t('proxmox.cloneLabel')}
              </Button>
            </div>
          )}

          {/* Result */}
          {result && (
            <Card>
              <CardHeader>
                <CardTitle icon={CheckCircle2}>{t('proxmox.resultLabel')}</CardTitle>
              </CardHeader>
              <CardContent>
                <pre className='text-xs text-gray-300 bg-gray-900 p-4 rounded-xl overflow-auto max-h-64'>
                  {JSON.stringify(result, null, 2)}
                </pre>
              </CardContent>
            </Card>
          )}

          {error && (
            <div className='flex items-start gap-2 p-4 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 text-sm'>
              <AlertCircle size={16} className='flex-shrink-0 mt-0.5' />
              {error}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TAB: Snapshoty
// ═══════════════════════════════════════════════════════════════════════════════

function TabSnapshots({ servers }: { servers: ServerEntry[] }) {
  const { t } = useTranslation();
  const [serverId, setServerId] = useState(servers[0]?.id);
  const [vmid, setVmid] = useState('');
  const [snapshots, setSnapshots] = useState<Array<{ name: string; vmstate: number; description?: string; uptime: number }>>([]);
  const [loadState, setLoadState] = useState<LoadState>({ kind: 'idle' });
  const loading = loadState.kind === 'loading';
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [result, setResult] = useState<Record<string, unknown> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmModal, setConfirmModal] = useState<{ name: string; onConfirm: () => void } | null>(null);

  const server = pickServer(servers, serverId);

  const loadSnapshots = useCallback(async () => {
    if (!server || !vmid) return;
    setLoadState({ kind: 'loading' });
    setSnapshots([]);
    const s = await callWithStatus<{ snapshots?: Array<{ name: string; vmstate: number; description?: string; uptime: number }> }>(
      'proxmox.snapshot',
      { action: 'list', server_id: server.id, vmid: Number(vmid) },
      (r) => setSnapshots(r.snapshots ?? []),
    );
    setLoadState(s);
  }, [server, vmid]);

  const handleCreate = useCallback(async () => {
    if (!server || !vmid || !newName) return;
    setActionLoading('create');
    setError(null);
    setResult(null);
    try {
      const r = await api.mcp<{ ok: boolean; error?: string } & Record<string, unknown>>('proxmox.snapshot', {
        action: 'create',
        server_id: server.id,
        vmid: Number(vmid),
        name: newName,
        description: newDesc || undefined,
      });
      if (r.ok) {
        setNewName('');
        setNewDesc('');
        await loadSnapshots();
        setResult(r);
      } else {
        setError(r.error ?? t('proxmox.unknownError'));
      }
    } catch (e) { setError(String(e)); }
    finally { setActionLoading(null); }
  }, [server, vmid, newName, newDesc, loadSnapshots]);

  const handleRevert = useCallback(async (name: string) => {
    if (!server || !vmid) return;
    setActionLoading(`revert-${name}`);
    try {
      const r = await api.mcp<{ ok: boolean; error?: string } & Record<string, unknown>>('proxmox.snapshot', {
        action: 'revert',
        server_id: server.id,
        vmid: Number(vmid),
        snapshot: name,
      });
      if (r.ok) await loadSnapshots();
      else setError(r.error ?? t('proxmox.unknownError'));
    } catch (e) { setError(String(e)); }
    finally { setActionLoading(null); }
  }, [server, vmid, loadSnapshots]);

  const handleDelete = useCallback(async (name: string) => {
    if (!server || !vmid) return;
    setActionLoading(`delete-${name}`);
    try {
      const r = await api.mcp<{ ok: boolean; error?: string } & Record<string, unknown>>('proxmox.snapshot', {
        action: 'delete',
        server_id: server.id,
        vmid: Number(vmid),
        snapshot: name,
      });
      if (r.ok) await loadSnapshots();
      else setError(r.error ?? t('proxmox.unknownError'));
    } catch (e) { setError(String(e)); }
    finally { setActionLoading(null); }
  }, [server, vmid, loadSnapshots]);

  return (
    <div className='space-y-6'>
      {/* Controls */}
      <div className='flex items-center gap-3'>
        <label className='text-sm text-gray-400'>{t('proxmox.serverLabel')}</label>
        <Select
          value={serverId ?? ''}
          onChange={(e) => setServerId(e.target.value)}
          options={servers.map((s) => ({ value: s.id, label: s.label }))}
          placeholder={t('proxmox.selectServerPlaceholder')}
        />
        <label className='text-sm text-gray-400'>{t('proxmox.vmidLabel')}</label>
        <Input
          value={vmid}
          onChange={(e) => setVmid(e.target.value)}
          placeholder='100'
          type='number'
          className='w-24'
        />
        <Button onClick={loadSnapshots} loading={loading} icon={<RefreshCw size={12} />}>{t('proxmox.loadButton')}</Button>
      </div>

      {!server && <EmptyState icon={<AlertCircle size={24} />} title={t('proxmox.noServerConfiguredTitle')} />}

      {server && vmid && <LoadBanner state={loadState} server={`${server.label} VM ${vmid}`} />}

      {server && vmid && (
        <>
          {/* Create snapshot */}
          <Card>
            <CardHeader>
              <CardTitle icon={Plus}>{t('proxmox.newSnapshotLabel')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className='flex gap-3'>
                <Input
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder={t('proxmox.snapshotNamePlaceholder')}
                  className='flex-1'
                />
                <Input
                  value={newDesc}
                  onChange={(e) => setNewDesc(e.target.value)}
                  placeholder={t('proxmox.descriptionOptionalPlaceholder')}
                  className='flex-1'
                />
                <Button onClick={handleCreate} loading={actionLoading === 'create'} icon={<Plus size={12} />}>{t('proxmox.createButton')}</Button>
              </div>
            </CardContent>
          </Card>

          {/* Snapshot list */}
          {snapshots.length === 0 && !loading ? (
            <EmptyState icon={<Database size={24} />} title={t('proxmox.noSnapshotsMessage')} />
          ) : (
            <DataTable
              columns={[
                { key: 'name', label: t('proxmox.colNazev') },
                { key: 'description', label: t('proxmox.colPopis'), render: (_, i) => snapshots[i].description ?? '—' },
                {
                  key: 'vmstate', label: t('proxmox.colStav'), render: (_, i) => {
                    const s = snapshots[i].vmstate;
                    return <Badge variant={s === 1 ? 'success' : s === 2 ? 'warning' : 'secondary'}>{s === 1 ? 'running' : s === 2 ? 'paused' : 'stopped'}</Badge>;
                  }
                },
                {
                  key: 'uptime', label: t('proxmox.colUptime'), render: (_, i) => fmtUptime(snapshots[i].uptime),
                },
                {
                  key: 'actions', label: '', render: (_, i) => (
                    <div className='flex items-center gap-2'>
                      <Button
                        variant='primary'
                        size='sm'
                        loading={actionLoading === `revert-${snapshots[i].name}`}
                        onClick={() => handleRevert(snapshots[i].name)}
                        icon={<RefreshCw size={12} />}
                      >
                        {t('proxmox.revertButton')}
                      </Button>
                      <Button
                        variant='danger'
                        size='sm'
                        loading={actionLoading === `delete-${snapshots[i].name}`}
                        onClick={() => setConfirmModal({ name: snapshots[i].name, onConfirm: () => { handleDelete(snapshots[i].name); setConfirmModal(null); } })}
                        icon={Trash2}
                      >
                        {t('proxmox.deleteButton')}
                      </Button>
                    </div>
                  ),
                },
              ]}
              data={snapshots}
              rowKey={(s) => s.name}
            />
          )}

          {error && (
            <div className='flex items-start gap-2 p-4 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 text-sm'>
              <AlertCircle size={16} className='flex-shrink-0 mt-0.5' />
              {error}
            </div>
          )}

          {result && (
            <Card>
              <CardHeader>
                <CardTitle icon={CheckCircle2}>{t('proxmox.resultLabel')}</CardTitle>
              </CardHeader>
              <CardContent>
                <pre className='text-xs text-gray-300 bg-gray-900 p-4 rounded-xl overflow-auto max-h-48'>
                  {JSON.stringify(result, null, 2)}
                </pre>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Confirmation modal */}
      <Modal
        open={!!confirmModal}
        onClose={() => setConfirmModal(null)}
        title={t('proxmox.deleteSnapshotConfirmTitle', { name: confirmModal?.name })}
        size='sm'
        footer={
          confirmModal ? (
            <>
              <Button variant='secondary' onClick={() => setConfirmModal(null)}>{t('proxmox.cancelButton')}</Button>
              <Button variant='danger' onClick={confirmModal.onConfirm}>{t('proxmox.deleteButton')}</Button>
            </>
          ) : undefined
        }
      >
        <p className='text-sm text-gray-300'>{t('proxmox.actionIrreversibleHint')}</p>
      </Modal>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TAB: Storage
// ═══════════════════════════════════════════════════════════════════════════════

function TabStorage({ servers }: { servers: ServerEntry[] }) {
  const { t } = useTranslation();
  const [serverId, setServerId] = useState(servers[0]?.id);
  const [storageList, setStorageList] = useState<Array<{ storage: string; type: string; shared?: number }>>([]);
  const [contentData, setContentData] = useState<Record<string, Array<{
    content: string; filename?: string; size?: string; size_bytes?: number; vmid?: number; parent?: string;
  }>>>({});
  const [loadState, setLoadState] = useState<LoadState>({ kind: 'idle' });
  const loading = loadState.kind === 'loading';
  const [selectedStorage, setSelectedStorage] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const server = pickServer(servers, serverId);

  const loadStorage = useCallback(async () => {
    if (!server) return;
    setLoadState({ kind: 'loading' });
    setStorageList([]);
    const s = await callWithStatus<{ defined?: Array<{ storage: string; type: string; shared?: number }> }>(
      'proxmox.audit',
      { action: 'storage', server_id: server.id },
      (r) => { setStorageList(r.defined ?? []); setContentData({}); },
    );
    setLoadState(s);
  }, [server]);

  const loadContent = useCallback(async (storage: string) => {
    if (!server) return;
    try {
      const r = await api.mcp<{
        ok: boolean;
        error?: string;
        backups?: Array<{
          filename: string;
          content: string;
          size: string;
          size_bytes: number;
          type: string;
          vmid?: number;
          parent?: string;
        }>;
      }>('proxmox.pbs_backup', {
        action: 'list',
        server_id: server.id,
        storage,
        content: 'vzdump',
      });
      if (r.ok) setContentData((prev) => ({ ...prev, [storage]: r.backups ?? [] }));
    } catch (e) { console.error(e); }
  }, [server]);

  useEffect(() => { loadStorage(); }, [loadStorage]);

  const handleDeleteBackup = useCallback(async (storage: string, filename: string) => {
    if (!server) return;
    setActionLoading(filename);
    try {
      const r = await api.mcp<{ ok: boolean; error?: string } & Record<string, unknown>>('proxmox.pbs_backup', {
        action: 'delete',
        server_id: server.id,
        storage,
        backup_id: filename,
      });
      if (r.ok) {
        await loadContent(storage);
        await loadStorage();
      } else {
        setError(r.error ?? t('proxmox.unknownError'));
      }
    } catch (e) { setError(String(e)); }
    finally { setActionLoading(null); }
  }, [server, loadContent, loadStorage]);

  return (
    <div className='space-y-6'>
      <div className='flex items-center gap-3'>
        <label className='text-sm text-gray-400'>{t('proxmox.serverLabel')}</label>
        <Select
          value={serverId ?? ''}
          onChange={(e) => setServerId(e.target.value)}
          options={servers.map((s) => ({ value: s.id, label: s.label }))}
          placeholder={t('proxmox.selectServerPlaceholder')}
        />
        <RefreshButton onRefresh={loadStorage} loading={loading} />
      </div>

      {!server && <EmptyState icon={<AlertCircle size={24} />} title={t('proxmox.noServerConfiguredTitle')} />}

      {server && <LoadBanner state={loadState} server={server.label} />}

      {server && (
        <>
          {/* Storage pools */}
          <SectionHeader title={t('proxmox.storagePoolsHeader')} />
          {storageList.length === 0 ? (
            <EmptyState icon={<Layers size={24} />} title={t('proxmox.noStorageDefinedMessage')} />
          ) : (
            <DataTable
              columns={[
                { key: 'storage', label: t('proxmox.colNazev') },
                { key: 'type', label: t('proxmox.colType') },
                {
                  key: 'shared', label: t('proxmox.colShared'), render: (_, i) => (
                    <Badge variant={storageList[i].shared ? 'success' : 'secondary'}>
                      {storageList[i].shared ? 'yes' : 'no'}
                    </Badge>
                  ),
                },
                {
                  key: 'actions', label: '', render: (_, i) => (
                    <Button
                      variant='primary'
                      size='sm'
                      onClick={() => { setSelectedStorage(storageList[i].storage); loadContent(storageList[i].storage); }}
                      icon={<Layers size={12} />}
                    >
                      {t('proxmox.contentButton')}
                    </Button>
                  ),
                },
              ]}
              data={storageList}
              rowKey={((s) => s.storage)}
            />
          )}

          {/* Content detail */}
          {selectedStorage && contentData[selectedStorage] !== undefined && (
            <div className='mt-6'>
              <SectionHeader title={t('proxmox.contentOfLabel', { storage: selectedStorage })} />
              {contentData[selectedStorage].length === 0 ? (
                <EmptyState icon={<Database size={24} />} title={t('proxmox.noRecordsTitle')} />
              ) : (
                <DataTable
                  columns={[
                    { key: 'filename', label: t('proxmox.colFilename') },
                    { key: 'content', label: t('proxmox.colObsah') },
                    {
                      key: 'size', label: t('proxmox.colVelikost'), render: (_, i) => {
                        const bytes = contentData[selectedStorage][i].size_bytes ?? 0;
                        return bytes >= GB ? `${(bytes / GB).toFixed(1)} GB` : `${Math.round(bytes / MB)} MB`;
                      }
                    },
                    { key: 'vmid', label: t('proxmox.colVmid') },
                    {
                      key: 'actions', label: '', render: (_, i) => (
                        <Button
                          variant='danger'
                          size='sm'
                          loading={actionLoading === contentData[selectedStorage][i].filename}
                          onClick={async () => {
                            const fn = contentData[selectedStorage][i].filename!;
                            if (await themedConfirm(t('proxmox.deleteBackupConfirm', { filename: fn }))) handleDeleteBackup(selectedStorage, fn);
                          }}
                          icon={Trash2}
                        >
                          {t('proxmox.deleteButton')}
                        </Button>
                      ),
                    },
                  ]}
                  data={contentData[selectedStorage]}
                  rowKey={((b) => b.filename!)}
                />
              )}
            </div>
          )}

          {error && (
            <div className='flex items-start gap-2 p-4 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 text-sm'>
              <AlertCircle size={16} className='flex-shrink-0 mt-0.5' />
              {error}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TAB: Zálohy (PBS)
// ═══════════════════════════════════════════════════════════════════════════════

function TabBackups({ servers }: { servers: ServerEntry[] }) {
  const { t } = useTranslation();
  const [serverId, setServerId] = useState(servers[0]?.id);
  const [storage, setStorage] = useState('');
  const [vmid, setVmid] = useState('');
  const [backups, setBackups] = useState<Array<{
    filename: string; content: string; size: string; size_bytes: number; type: string; vmid?: number; parent?: string;
  }>>([]);
  const [loadState, setLoadState] = useState<LoadState>({ kind: 'idle' });
  const loading = loadState.kind === 'loading';
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [result, setResult] = useState<Record<string, unknown> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pbsStorages, setPbsStorages] = useState<Array<{ storage: string; type: string; enabled: boolean }>>([]);

  const server = pickServer(servers, serverId);

  const loadBackups = useCallback(async () => {
    if (!server || !storage) return;
    setLoadState({ kind: 'loading' });
    setBackups([]);
    const s = await callWithStatus<{ backups?: Array<{ filename: string; content: string; size: string; size_bytes: number; type: string; vmid?: number; parent?: string }> }>(
      'proxmox.pbs_backup',
      { action: 'list', server_id: server.id, storage, content: 'vzdump' },
      (r) => setBackups(r.backups ?? []),
    );
    setLoadState(s);
  }, [server, storage]);

  const handleCreateBackup = useCallback(async () => {
    if (!server || !storage || !vmid) return;
    setActionLoading('create');
    setError(null);
    setResult(null);
    try {
      const r = await api.mcp<{ ok: boolean; error?: string } & Record<string, unknown>>('proxmox.pbs_backup', {
        action: 'create',
        server_id: server.id,
        storage,
        vmid: Number(vmid),
        mode: 'snapshot',
        bandwidthlimit: 0,
      });
      if (r.ok) {
        setResult(r);
        await loadBackups();
      } else {
        setError(r.error ?? t('proxmox.unknownError'));
      }
    } catch (e) { setError(String(e)); }
    finally { setActionLoading(null); }
  }, [server, storage, vmid, loadBackups]);

  const handleRestore = useCallback(async (filename: string) => {
    if (!server || !storage) return;
    const targetVmid = await themedPrompt(t('proxmox.restoreTargetVmidPrompt'));
    if (!targetVmid) return;
    setActionLoading('restore');
    setError(null);
    try {
      const r = await api.mcp<{ ok: boolean; error?: string } & Record<string, unknown>>('proxmox.pbs_backup', {
        action: 'restore',
        server_id: server.id,
        storage,
        backup_id: filename,
        vmid: Number(targetVmid),
      });
      if (r.ok) setResult(r);
      else setError(r.error ?? t('proxmox.unknownError'));
    } catch (e) { setError(String(e)); }
    finally { setActionLoading(null); }
  }, [server, storage]);

  const handleDeleteBackup = useCallback(async (filename: string) => {
    if (!server || !storage) return;
    setActionLoading(filename);
    try {
      const r = await api.mcp<{ ok: boolean; error?: string } & Record<string, unknown>>('proxmox.pbs_backup', {
        action: 'delete',
        server_id: server.id,
        storage,
        backup_id: filename,
      });
      if (r.ok) await loadBackups();
      else setError(r.error ?? t('proxmox.unknownError'));
    } catch (e) { setError(String(e)); }
    finally { setActionLoading(null); }
  }, [server, storage, loadBackups]);

  // Load PBS storages on server change
  useEffect(() => {
    if (!server) return;
    (async () => {
      const r = await api.mcp<{
        ok: boolean;
        error?: string;
        pbs_storages?: Array<{ storage: string; type: string; enabled: boolean }>;
      }>('proxmox.pbs_check', { action: 'check' });
      if (r.ok) {
        setPbsStorages(r.pbs_storages ?? []);
        if (r.pbs_storages?.length) setStorage(r.pbs_storages[0].storage);
      }
    })();
  }, [server]);

  useEffect(() => { if (storage) loadBackups(); }, [storage, loadBackups]);

  return (
    <div className='space-y-6'>
      <div className='flex items-center gap-3'>
        <label className='text-sm text-gray-400'>{t('proxmox.serverLabel')}</label>
        <Select
          value={serverId ?? ''}
          onChange={(e) => setServerId(e.target.value)}
          options={servers.map((s) => ({ value: s.id, label: s.label }))}
          placeholder={t('proxmox.selectServerPlaceholder')}
        />
        {pbsStorages.length > 0 && (
          <>
            <label className='text-sm text-gray-400'>{t('proxmox.pbsStorageLabel')}</label>
            <Select
              value={storage}
              onChange={(e) => setStorage(e.target.value)}
              options={pbsStorages.map((s) => ({ value: s.storage, label: s.storage }))}
              placeholder={t('proxmox.selectStoragePlaceholder')}
            />
          </>
        )}
        {storage && <RefreshButton onRefresh={loadBackups} loading={loading} />}
      </div>

      {!server && <EmptyState icon={<AlertCircle size={24} />} title={t('proxmox.noServerConfiguredTitle')} />}

      {server && storage && <LoadBanner state={loadState} server={`${server.label} / ${storage}`} />}

      {server && (
        <>
          {/* Create backup */}
          <Card>
            <CardHeader>
              <CardTitle icon={Database}>{t('proxmox.newBackupLabel')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className='flex gap-3'>
                <label className='text-sm text-gray-400 self-center'>{t('proxmox.vmidLabel')}</label>
                <Input
                  value={vmid}
                  onChange={(e) => setVmid(e.target.value)}
                  placeholder='100'
                  type='number'
                  className='w-24'
                />
                <Button onClick={handleCreateBackup} loading={actionLoading === 'create'} icon={<Database size={12} />}>
                  {t('proxmox.backupButton')}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Backup list */}
          {backups.length === 0 && !loading ? (
            <EmptyState icon={<Database size={24} />} title={t('proxmox.noBackupsMessage')} />
          ) : (
            <DataTable
              columns={[
                { key: 'filename', label: t('proxmox.colFilename') },
                { key: 'vmid', label: t('proxmox.colVmid') },
                { key: 'content', label: t('proxmox.colObsah') },
                { key: 'size', label: t('proxmox.colVelikost') },
                {
                  key: 'actions', label: '', render: (_, i) => (
                    <div className='flex items-center gap-2'>
                      <Button
                        variant='primary'
                        size='sm'
                        loading={actionLoading === backups[i].filename && actionLoading !== 'create'}
                        onClick={() => handleRestore(backups[i].filename)}
                        icon={<RefreshCw size={12} />}
                      >
                        {t('proxmox.restoreButton')}
                      </Button>
                      <Button
                        variant='danger'
                        size='sm'
                        loading={actionLoading === backups[i].filename}
                        onClick={() => handleDeleteBackup(backups[i].filename)}
                        icon={Trash2}
                      >
                        {t('proxmox.deleteButton')}
                      </Button>
                    </div>
                  ),
                },
              ]}
              data={backups}
              rowKey={((b) => b.filename)}
            />
          )}

          {error && (
            <div className='flex items-start gap-2 p-4 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 text-sm'>
              <AlertCircle size={16} className='flex-shrink-0 mt-0.5' />
              {error}
            </div>
          )}

          {result && (
            <Card>
              <CardHeader>
                <CardTitle icon={CheckCircle2}>{t('proxmox.resultLabel')}</CardTitle>
              </CardHeader>
              <CardContent>
                <pre className='text-xs text-gray-300 bg-gray-900 p-4 rounded-xl overflow-auto max-h-48'>
                  {JSON.stringify(result, null, 2)}
                </pre>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TAB: Nastavení (servery + PBS)
// ═══════════════════════════════════════════════════════════════════════════════

function TabSettings({ servers }: { servers: ServerEntry[] }) {
  const { t } = useTranslation();
  const [serversList, setServersList] = useState<ServerEntry[]>(servers);
  const [reloadKey, setReloadKey] = useState(0);
  const [editServer, setEditServer] = useState<ServerEntry | null>(null);
  const [pbsAction, setPbsAction] = useState<'check' | 'configure'>('check');
  const [pbsHost, setPbsHost] = useState('');
  const [pbsPort, setPbsPort] = useState('8007');
  const [pbsUser, setPbsUser] = useState('');
  const [pbsPass, setPbsPass] = useState('');
  const [pbsDatastore, setPbsDatastore] = useState('datastore');
  const [pbsResult, setPbsResult] = useState<Record<string, unknown> | null>(null);
  const [pbsLoading, setPbsLoading] = useState(false);
  const [pbsError, setPbsError] = useState<string | null>(null);

  const currentServers = serversList.length > 0 ? serversList : servers;

  // Reload servers when key changes
  useEffect(() => {
    (async () => {
      const r = await api.mcp<{ ok: boolean; error?: string; servers?: ServerEntry[] }>(
        'proxmox.servers_list',
        {},
      );
      if (r.ok) setServersList(r.servers ?? []);
    })();
  }, [reloadKey]);

  const handleSaveServer = useCallback(async (server: ServerEntry) => {
    const r = await api.mcp<{ ok: boolean; error?: string }>('proxmox.servers_manage', {
      action: 'update',
      server_id: server.id,
      label: server.label,
      url: server.url,
      api_token: server.api_token || undefined,
      verify_ssl: server.verify_ssl,
    });
    if (r.ok) {
      setEditServer(null);
      setReloadKey((k) => k + 1);
    } else {
      alert(t('proxmox.genericErrorAlert', { error: r.error }));
    }
  }, []);

  const handleDeleteServer = useCallback(async (id: string) => {
    if (!(await themedConfirm(t('proxmox.deleteServerConfirm')))) return;
    const r = await api.mcp<{ ok: boolean; error?: string }>('proxmox.servers_manage', { action: 'delete', server_id: id });
    if (r.ok) setReloadKey((k) => k + 1);
    else alert(t('proxmox.genericErrorAlert', { error: r.error }));
  }, []);

  // Add new server modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [addUrl, setAddUrl] = useState('https://');
  const [addToken, setAddToken] = useState('');
  const [addLabel, setAddLabel] = useState('');
  const [addServerId, setAddServerId] = useState('');
  const [addVerifySsl, setAddVerifySsl] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  const resetAddForm = useCallback(() => {
    setAddUrl('https://');
    setAddToken('');
    setAddLabel('');
    setAddServerId('');
    setAddVerifySsl(false);
    setAddError(null);
  }, []);

  const handleAddServer = useCallback(async () => {
    setAddLoading(true);
    setAddError(null);
    try {
      const r = await api.mcp<{ ok: boolean; error?: string }>('proxmox.server', {
        action: 'add',
        server_id: addServerId.trim() || undefined,
        url: addUrl.trim(),
        api_token: addToken.trim(),
        label: addLabel.trim() || undefined,
        verify_ssl: addVerifySsl,
      });
      if (r.ok) {
        setShowAddModal(false);
        resetAddForm();
        setReloadKey((k) => k + 1);
      } else {
        setAddError((r as { error?: string }).error ?? t('proxmox.unknownError'));
      }
    } catch (e) {
      setAddError(e instanceof Error ? e.message : String(e));
    } finally {
      setAddLoading(false);
    }
  }, [addServerId, addUrl, addToken, addLabel, addVerifySsl, resetAddForm]);

  // Publish toggle per server
  const [publishLoading, setPublishLoading] = useState<string | null>(null);
  const [publishMsg, setPublishMsg] = useState<{ id: string; ok: boolean; text: string } | null>(null);

  const handlePublish = useCallback(async (id: string, enable: boolean) => {
    setPublishLoading(id);
    setPublishMsg(null);
    try {
      const r = await api.mcp<{ ok: boolean; error?: string }>(
        enable ? 'proxmox.publish' : 'proxmox.unpublish',
        { server_id: id },
      );
      setPublishMsg({
        id,
        ok: r.ok === true,
        text: r.ok
          ? (enable ? t('proxmox.publishedToast', { url: (r as { url?: string }).url ?? t('proxmox.noUrlLabel') }) : t('proxmox.unpublishedToast'))
          : t('proxmox.publishErrorToast', { error: (r as { error?: string }).error ?? t('proxmox.unknownError') }),
      });
    } catch (e) {
      setPublishMsg({ id, ok: false, text: e instanceof Error ? e.message : String(e) });
    } finally {
      setPublishLoading(null);
    }
  }, []);

  const handlePbsAction = useCallback(async () => {
    setPbsLoading(true);
    setPbsError(null);
    setPbsResult(null);
    try {
      const args: Record<string, unknown> = { action: pbsAction };
      if (pbsAction === 'configure') {
        args.pbs_host = pbsHost;
        args.pbs_port = Number(pbsPort) || 8007;
        args.pbs_username = pbsUser;
        if (pbsPass) args.pbs_password = pbsPass;
        args.datastore = pbsDatastore;
      }
      const r = await api.mcp<{ ok: boolean; error?: string } & Record<string, unknown>>('proxmox.pbs_check', args);
      if (r.ok) setPbsResult(r);
      else setPbsError(r.error ?? t('proxmox.unknownError'));
    } catch (e) { setPbsError(String(e)); }
    finally { setPbsLoading(false); }
  }, [pbsAction, pbsHost, pbsPort, pbsUser, pbsPass, pbsDatastore]);

  return (
    <div className='space-y-8'>
      {/* ── Server CRUD ─────────────────────────────────────────────────── */}
      <div>
        <SectionHeader
          title={t('proxmox.proxmoxServersHeader')}
          description={t('proxmox.proxmoxServersDescription')}
          actions={
            <Button
              variant='primary'
              size='sm'
              icon={<Plus size={12} />}
              onClick={() => { resetAddForm(); setShowAddModal(true); }}
            >
              {t('proxmox.addServerButton')}
            </Button>
          }
        />

        {currentServers.length === 0 ? (
          <EmptyState
            icon={<Server size={24} />}
            title={t('proxmox.noServersTitle')}
            description={t('proxmox.noServersDescription')}
          />
        ) : (
          <DataTable
            columns={[
              { key: 'label', label: t('proxmox.colPopisek') },
              { key: 'url', label: t('proxmox.colUrl') },
              {
                key: 'verify_ssl', label: 'SSL', render: (_, i) => (
                  <Badge variant={currentServers[i].verify_ssl ? 'success' : 'warning'}>
                    {currentServers[i].verify_ssl ? 'verify' : 'skip'}
                  </Badge>
                ),
              },
              {
                key: 'updated_at', label: t('proxmox.colAktualizace'), render: (_, i) =>
                  currentServers[i].updated_at ? fmtDate(currentServers[i].updated_at) : '—',
              },
              {
                key: 'actions', label: '', render: (_, i) => (
                  <div className='flex items-center gap-2 flex-wrap'>
                    <Button
                      variant='primary'
                      size='sm'
                      onClick={() => setEditServer(currentServers[i])}
                      icon={<Settings size={12} />}
                    >
                      {t('proxmox.editButton')}
                    </Button>
                    <Button
                      variant='success'
                      size='sm'
                      loading={publishLoading === currentServers[i].id}
                      onClick={() => handlePublish(currentServers[i].id, true)}
                      icon={<Globe size={12} />}
                    >
                      {t('proxmox.publishButton')}
                    </Button>
                    <Button
                      variant='secondary'
                      size='sm'
                      loading={publishLoading === currentServers[i].id}
                      onClick={() => handlePublish(currentServers[i].id, false)}
                    >
                      {t('proxmox.unpublishButton')}
                    </Button>
                    <Button
                      variant='danger'
                      size='sm'
                      onClick={() => handleDeleteServer(currentServers[i].id)}
                      icon={<Trash2 size={12} />}
                    >
                      {t('proxmox.deleteButton')}
                    </Button>
                  </div>
                ),
              },
            ]}
            data={currentServers}
            rowKey={((s) => s.id)}
          />
        )}

        {publishMsg && (
          <div className={`mt-3 rounded-md border px-3 py-2 text-sm ${publishMsg.ok ? 'border-emerald-700/40 bg-emerald-900/20 text-emerald-300' : 'border-red-700/40 bg-red-900/20 text-red-300'}`}>
            <strong>{publishMsg.id}:</strong> {publishMsg.text}
          </div>
        )}
      </div>

      {/* ── Edit server modal ───────────────────────────────────────────── */}
      <Modal
        open={!!editServer}
        onClose={() => setEditServer(null)}
        title={t('proxmox.editProxmoxServerModalTitle')}
        size='lg'
        footer={
          editServer ? (
            <>
              <Button variant='secondary' onClick={() => setEditServer(null)}>{t('proxmox.cancelButton')}</Button>
              <Button onClick={() => handleSaveServer(editServer)} icon={<Settings size={12} />}>{t('proxmox.saveButton')}</Button>
            </>
          ) : undefined
        }
      >
        {editServer && (
          <div className='space-y-4'>
            <div>
              <label className='text-xs text-gray-500 mb-1 block'>{t('proxmox.popisekLabel')}</label>
              <Input
                value={editServer.label}
                onChange={(e) => setEditServer({ ...editServer, label: e.target.value })}
              />
            </div>
            <div>
              <label className='text-xs text-gray-500 mb-1 block'>{t('proxmox.urlLabel')}</label>
              <Input
                value={editServer.url}
                onChange={(e) => setEditServer({ ...editServer, url: e.target.value })}
              />
            </div>
            <div>
              <label className='text-xs text-gray-500 mb-1 block'>{t('proxmox.apiTokenOptionalLabel')}</label>
              <Input
                value={editServer.api_token}
                onChange={(e) => setEditServer({ ...editServer, api_token: e.target.value })}
                placeholder='root@pam!proxmox=uuid:token'
              />
            </div>
            <div className='flex items-center gap-2'>
              <input
                type='checkbox'
                checked={editServer.verify_ssl}
                onChange={(e) => setEditServer({ ...editServer, verify_ssl: e.target.checked })}
                className='rounded border-gray-700 bg-gray-900'
              />
              <label className='text-sm text-gray-300'>{t('proxmox.verifySslLabel')}</label>
            </div>
          </div>
        )}
      </Modal>

      {/* ── PBS Configuration ───────────────────────────────────────────── */}
      <div>
        <SectionHeader
          title={t('proxmox.pbsSectionHeader')}
          description={t('proxmox.pbsSectionDescription')}
        />

        <div className='flex gap-2 mb-4'>
          <Button variant={pbsAction === 'check' ? 'primary' : 'secondary'} onClick={() => setPbsAction('check')}>
            {t('proxmox.checkAvailabilityButton')}
          </Button>
          <Button variant={pbsAction === 'configure' ? 'primary' : 'secondary'} onClick={() => setPbsAction('configure')}>
            {t('proxmox.connectPbsButton')}
          </Button>
        </div>

        <p className='text-xs text-gray-500 mb-3'>
          {pbsAction === 'check'
            ? t('proxmox.pbsCheckHint')
            : t('proxmox.pbsConfigureHint')}
        </p>

        {pbsAction === 'configure' && (
          <div className='space-y-4 mb-4'>
            <div className='grid grid-cols-2 gap-4'>
              <div>
                <label className='text-xs text-gray-500 mb-1 block'>{t('proxmox.pbsHostLabel')}</label>
                <Input value={pbsHost} onChange={(e) => setPbsHost(e.target.value)} placeholder='pbs.example.com' />
              </div>
              <div>
                <label className='text-xs text-gray-500 mb-1 block'>{t('proxmox.portLabel')}</label>
                <Input value={pbsPort} onChange={(e) => setPbsPort(e.target.value)} type='number' />
              </div>
            </div>
            <div className='grid grid-cols-2 gap-4'>
              <div>
                <label className='text-xs text-gray-500 mb-1 block'>{t('proxmox.userFieldLabel')}</label>
                <Input value={pbsUser} onChange={(e) => setPbsUser(e.target.value)} placeholder='root@pam' />
              </div>
              <div>
                <label className='text-xs text-gray-500 mb-1 block'>{t('proxmox.datastoreLabel')}</label>
                <Input value={pbsDatastore} onChange={(e) => setPbsDatastore(e.target.value)} placeholder='datastore' />
              </div>
            </div>
            <div>
              <label className='text-xs text-gray-500 mb-1 block'>{t('proxmox.pbsPasswordLabel')}</label>
              <Input value={pbsPass} onChange={(e) => setPbsPass(e.target.value)} type='password' placeholder={t('proxmox.pbsPasswordPlaceholder')} />
            </div>
          </div>
        )}

        <Button onClick={handlePbsAction} loading={pbsLoading} icon={<Shield size={12} />}>
          {pbsAction === 'check' ? t('proxmox.checkPbsButton') : t('proxmox.configurePbsButton')}
        </Button>

        {pbsError && (
          <div className='flex items-start gap-2 mt-4 p-4 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 text-sm'>
            <AlertCircle size={16} className='flex-shrink-0 mt-0.5' />
            {pbsError}
          </div>
        )}

        {pbsResult && (
          <Card className='mt-4'>
            <CardHeader>
              <CardTitle icon={CheckCircle2}>{t('proxmox.resultLabel')}</CardTitle>
            </CardHeader>
            <CardContent>
              <pre className='text-xs text-gray-300 bg-gray-900 p-4 rounded-xl overflow-auto max-h-64'>
                {JSON.stringify(pbsResult, null, 2)}
              </pre>
            </CardContent>
          </Card>
        )}
      </div>

      {/* ── Add server modal ─────────────────────────────────────────────── */}
      <Modal
        open={showAddModal}
        onClose={() => { setShowAddModal(false); resetAddForm(); }}
        title={t('proxmox.addProxmoxServerModalTitle')}
        size='md'
        footer={
          <>
            <Button variant='secondary' onClick={() => { setShowAddModal(false); resetAddForm(); }}>{t('proxmox.cancelButton')}</Button>
            <Button
              variant='primary'
              loading={addLoading}
              disabled={!addUrl.trim() || !addToken.trim()}
              onClick={handleAddServer}
              icon={<Plus size={12} />}
            >
              {t('proxmox.addButton')}
            </Button>
          </>
        }
      >
        <div className='space-y-3'>
          <div>
            <label className='block text-xs text-gray-400 mb-1'>{t('proxmox.urlProxmoxApiLabel')}</label>
            <Input value={addUrl} onChange={(e) => setAddUrl(e.target.value)} placeholder='https://10.119.255.29:8006' />
          </div>
          <div>
            <label className='block text-xs text-gray-400 mb-1'>{t('proxmox.apiTokenRequiredLabel')}</label>
            <Input
              value={addToken}
              onChange={(e) => setAddToken(e.target.value)}
              placeholder='root@pam!acm=UUID-token-secret'
            />
            <p className='mt-1 text-[10px] text-gray-500'>
              {t('proxmox.tokenFormatHintPre')} <code className='text-gray-400'>USER@REALM!TOKENID=SECRET</code>. {t('proxmox.tokenFormatHintPost')}
            </p>
          </div>
          <div className='grid grid-cols-2 gap-3'>
            <div>
              <label className='block text-xs text-gray-400 mb-1'>{t('proxmox.labelOptionalLabel')}</label>
              <Input value={addLabel} onChange={(e) => setAddLabel(e.target.value)} placeholder={t('proxmox.labelPlaceholderExample')} />
            </div>
            <div>
              <label className='block text-xs text-gray-400 mb-1'>{t('proxmox.idSlugOptionalLabel')}</label>
              <Input value={addServerId} onChange={(e) => setAddServerId(e.target.value)} placeholder={t('proxmox.autoFromUrlPlaceholder')} />
            </div>
          </div>
          <label className='flex items-center gap-2 text-sm text-gray-300'>
            <input type='checkbox' checked={addVerifySsl} onChange={(e) => setAddVerifySsl(e.target.checked)} />
            {t('proxmox.verifySslCheckboxLabel')}
          </label>
          {addError && (
            <div className='rounded-md border border-red-700/40 bg-red-900/20 px-3 py-2 text-sm text-red-300'>
              <AlertCircle size={14} className='inline mr-1' /> {addError}
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TAB: OMA — audit log (schválení přes ACMLink / centrum oznámení)
// ═══════════════════════════════════════════════════════════════════════════════

function TabOma() {
  const { t } = useTranslation();
  const [status, setStatus] = useState<string>('all');
  const [rows, setRows] = useState<OmaRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [linkPairings, setLinkPairings] = useState<
    Array<{ id: string; label?: string | null; last_seen_at?: string | null; enabled?: boolean }>
  >([]);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.mcp<{ ok?: boolean; requests?: OmaRequest[] }>(
        'proxmox.request_list',
        { status, limit: 50 },
      );
      setRows(r?.requests ?? []);
      try {
        const p = await api.mcp<{
          ok?: boolean;
          pairings?: Array<{
            id: string;
            label?: string | null;
            last_seen_at?: string | null;
            enabled?: boolean;
          }>;
        }>('acm-link.pair_list', {});
        setLinkPairings(p?.pairings ?? []);
      } catch {
        setLinkPairings([]);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const linkOnline = linkPairings.some((p) => {
    if (!p.enabled || !p.last_seen_at) return false;
    const age = Date.now() - Date.parse(p.last_seen_at);
    return Number.isFinite(age) && age < 15 * 60_000;
  });

  return (
    <div className='space-y-4'>
      <div className='flex flex-wrap items-center gap-2'>
        <Select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          options={[
            { value: 'all', label: t('proxmox.omaAll', 'Vše (log)') },
            { value: 'pending', label: t('proxmox.omaPending', 'Čeká na rozhodnutí') },
            { value: 'approved', label: t('proxmox.omaApproved', 'Schváleno') },
            { value: 'denied', label: t('proxmox.omaDenied', 'Zamítnuto') },
            { value: 'executed', label: t('proxmox.omaExecutedStatus', 'Vykonáno') },
          ]}
        />
        <RefreshButton onRefresh={() => void reload()} />
        <Badge variant={linkOnline ? 'success' : 'secondary'}>
          ACMLink {linkOnline
            ? t('proxmox.linkOnline', 'online')
            : t('proxmox.linkOffline', 'offline / nespárováno')}
        </Badge>
      </div>

      <Card>
        <CardHeader>
          <CardTitle icon={Shield}>
            {t('proxmox.omaLogTitle', 'OMA log — schválení / zamítnutí')}
          </CardTitle>
        </CardHeader>
        <CardContent className='text-sm text-gray-400 space-y-2'>
          <p>
            {t(
              'proxmox.omaLogHint1',
              'Tato záložka je audit log (pending → schváleno/zamítnuto/vykonáno). ' +
                'Schválit nebo zamítnout se dělá přes ACMLink (prohlížeč operátora), ne odsud.',
            )}
          </p>
          <p className='text-xs text-gray-500'>
            {t(
              'proxmox.omaLogHint2',
              'Alternativa v ACM: fronta v centru oznámení pro adminy s oprávněním ' +
                '(notification_center) — kumuluje co mají ke schválení. Zatím se buduje.',
            )}
          </p>
          {!linkOnline && (
            <p className='text-amber-400/90 text-xs'>
              {t(
                'proxmox.linkDecideHint',
                'Pro schválení přes ACMLink musí být spárované rozšíření online (last_seen < 15 min).',
              )}
            </p>
          )}
        </CardContent>
      </Card>

      {loading
        ? <div className='text-sm text-gray-500'>{t('common.loading', 'Načítám…')}</div>
        : rows.length === 0
        ? (
          <EmptyState
            icon={<Shield size={24} />}
            title={t('proxmox.omaEmpty', 'Žádné OMA záznamy v tomto filtru')}
          />
        )
        : (
          <div className='space-y-2'>
            {rows.map((req) => (
              <div
                key={req.id}
                className='rounded-xl border border-gray-800 bg-gray-950/40 px-3 py-3'
              >
                <div className='flex flex-wrap items-center gap-2'>
                  <Badge
                    variant={
                      req.status === 'pending'
                        ? 'warning'
                        : req.status === 'approved'
                        ? 'info'
                        : req.status === 'executed'
                        ? 'success'
                        : req.status === 'denied'
                        ? 'error'
                        : 'secondary'
                    }
                  >
                    {req.status}
                  </Badge>
                  <span className='font-mono text-xs text-white truncate'>
                    {req.tool} / {req.action}
                  </span>
                  <span className='text-xs text-gray-500'>{req.server_id}</span>
                  {req.risk && <Badge variant='secondary'>{req.risk}</Badge>}
                </div>
                <div className='text-[11px] text-gray-500 mt-1 truncate'>
                  {req.reason || '—'} · {req.requested_by?.slice(0, 8)} ·{' '}
                  {req.requested_at ? fmtDate(req.requested_at) : ''}
                  {req.approved_by ? ` · decided by ${req.approved_by.slice(0, 8)}` : ''}
                  {req.id ? ` · ${req.id.slice(0, 8)}` : ''}
                </div>
                {req.error && (
                  <div className='text-[11px] text-gray-400 mt-0.5 break-all'>{req.error}</div>
                )}
                {req.status === 'pending' && (
                  <div className='text-[10px] text-amber-500/90 mt-1'>
                    {t(
                      'proxmox.omaWaitingLink',
                      'Čeká na rozhodnutí přes ACMLink (nebo centrum oznámení).',
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════════════════════════════

export default function ProxmoxPage() {
  const { t } = useTranslation();
  const { servers, reload } = useServers();

  return (
    <div className='min-h-screen bg-black text-white'>
      <PageHeader
        title={t('proxmox.pageTitle')}
        description={t('proxmox.pageDescription')}
        actions={
          <RefreshButton onRefresh={reload} />
        }
      />

      <div className='max-w-7xl mx-auto px-4 py-6'>
        <Tabs defaultTab='overview'>
          <TabList>
            <Tab id='overview'>{t('proxmox.tabOverview')}</Tab>
            <Tab id='vm-ct'>{t('proxmox.tabVmCt')}</Tab>
            <Tab id='oma'>{t('proxmox.tabOma', 'OMA')}</Tab>
            <Tab id='create'>{t('proxmox.tabCreate')}</Tab>
            <Tab id='snapshots'>{t('proxmox.tabSnapshots')}</Tab>
            <Tab id='storage'>{t('proxmox.tabStorage')}</Tab>
            <Tab id='backups'>{t('proxmox.tabBackups')}</Tab>
            <Tab id='settings'>{t('proxmox.tabSettings')}</Tab>
          </TabList>

          <TabPanel id='overview'><TabOverview servers={servers} /></TabPanel>
          <TabPanel id='vm-ct'><TabVmCt servers={servers} /></TabPanel>
          <TabPanel id='oma'><TabOma /></TabPanel>
          <TabPanel id='create'><TabCreate servers={servers} /></TabPanel>
          <TabPanel id='snapshots'><TabSnapshots servers={servers} /></TabPanel>
          <TabPanel id='storage'><TabStorage servers={servers} /></TabPanel>
          <TabPanel id='backups'><TabBackups servers={servers} /></TabPanel>
          <TabPanel id='settings'><TabSettings servers={servers} /></TabPanel>
        </Tabs>
      </div>
    </div>
  );
}
